import { get, send } from "../api.js";

export type WaitlistRequestStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "fulfilled"
  | "withdrawn"
  | "lapsed"
  | "superseded";

export interface FamilyRetentionFollowUpPreferences {
  enabled: boolean;
  retentionFollowUpsEnabled: boolean;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  timeZone: "Europe/London";
}

export interface FamilyRetentionFollowUpPreferencesInput {
  retentionFollowUpsEnabled: boolean;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
}

export interface WaitlistPattern {
  weekday: number;
  startsMinutes: number;
  endsMinutes: number;
}

export interface WaitlistRequest {
  id: string;
  studentId: string;
  studentName: string;
  tutorId: string;
  tutorName: string;
  serviceId: number;
  serviceName: string;
  status: WaitlistRequestStatus;
  decisionReason?: string | null;
  pausedAt?: string | null;
  shortNoticeOptIn?: boolean;
  createdAt: string;
  decidedAt: string | null;
  kind?: "pattern" | "specific_time";
  purpose?: "book" | "move" | null;
  requestedStartsAt?: string | null;
  sourceStartsAt?: string | null;
  fallback?: "keep" | "cancel" | null;
  actBy?: string | null;
  shortNoticeApprovedAt?: string | null;
  /** Specific-time requests only: whether this viewer is the tutor who may approve. */
  canApprove?: boolean;
  patterns: WaitlistPattern[];
}

export interface WaitlistNotification {
  id: string;
  status: "pending";
  offeredAt: string;
  expiresAt: string | null;
  studentId: string;
  studentName: string;
  serviceName: string;
  tutorName: string;
  startsAt: string;
  endsAt: string;
  unread: boolean;
  kind?: "pattern" | "specific_time";
  purpose?: "book" | "move" | null;
  sourceStartsAt?: string | null;
  fallback?: "keep" | "cancel" | null;
  cancellationAt?: string | null;
}

export interface WaitlistOfferContext {
  id: string; studentId: string; status: string;
  startsAt: string; endsAt: string; expiresAt: string | null;
  serviceName: string; tutorName: string;
}

export interface WaitlistShortNoticeApproval {
  id: string;
  status: "pending" | "approved";
  offerId: string | null;
  offerStatus: "pending" | "accepted" | "rejected" | "expired" | "failed" | null;
  matchId: string;
  requestId: string;
  studentId: string;
  studentName: string;
  tutorId: string;
  tutorName: string;
  serviceId: number;
  serviceName: string;
  startsAt: string;
  endsAt: string;
  requestedAt: string;
  expiresAt: string;
}

export interface RequestableTime {
  startsAt: string;
  endsAt: string;
  state: "free" | "taken";
  subjectToApproval: boolean;
}

export type FamilyNotificationPreferences =
  | { enabled: false }
  | {
      enabled: true;
      shortNoticeOffersEnabled: boolean;
      practiceAssignmentBellEnabled: boolean;
      practiceAssignmentBellAvailable: boolean;
      weeklySummaryEnabled: boolean;
      weeklySummaryAvailable: boolean;
      concertInvitationEmailEnabled: boolean;
      concertInvitationEmailAvailable: boolean;
      quietHoursStart: string | null;
      quietHoursEnd: string | null;
      timeZone: "Europe/London";
    };

export type FamilyNotificationPreferencesInput = {
  shortNoticeOffersEnabled: boolean;
  practiceAssignmentBellEnabled?: boolean;
  weeklySummaryEnabled?: boolean;
  concertInvitationEmailEnabled?: boolean;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
};
export const waitlistApi = {
  notificationPreferences: (studentId: string) =>
    get<FamilyNotificationPreferences>(
      `/waitlist/notification-preferences/${encodeURIComponent(studentId)}`,
    ),

  saveNotificationPreferences: (
    studentId: string,
    input: FamilyNotificationPreferencesInput,
  ) =>
    send<Extract<FamilyNotificationPreferences, { enabled: true }>>(
      "PUT",
      `/waitlist/notification-preferences/${encodeURIComponent(studentId)}`,
      input,
    ),

  config: () =>
    get<{ enabled: boolean; noticeHours: number }>("/waitlist/config"),
  requestableTimes: (
    tutorId: string,
    serviceId: number,
    studentId: string,
    date: string,
    bookingId?: string,
  ) =>
    get<{ times: RequestableTime[] }>(
      `/waitlist/tutors/${encodeURIComponent(tutorId)}/requestable-times`,
      { serviceId: String(serviceId), studentId, date, bookingId },
    ),
  createTimeRequest: (
    input:
      | {
          kind: "specific_time";
          studentId: string;
          tutorId: string;
          serviceId: number;
          serviceName: string;
          startsAt: string;
          purpose: "book";
        }
      | {
          kind: "specific_time";
          studentId: string;
          tutorId: string;
          serviceId: number;
          serviceName: string;
          startsAt: string;
          purpose: "move";
          bookingId: string;
          fallback: "keep" | "cancel";
        },
  ) =>
    send<{ subjectToApproval: boolean }>("POST", "/waitlist/requests", input),
  withdrawRequest: (id: string) =>
    send("POST", `/waitlist/requests/${encodeURIComponent(id)}/withdraw`),
  updateRequest: (id: string, input: {
    tutorId: string;
    serviceId: number;
    patterns: WaitlistPattern[];
    shortNoticeOptIn: boolean;
  }) => send("PATCH", `/waitlist/requests/${encodeURIComponent(id)}`, input),
  pauseRequest: (id: string) =>
    send("POST", `/waitlist/requests/${encodeURIComponent(id)}/pause`),
  resumeRequest: (id: string) =>
    send("POST", `/waitlist/requests/${encodeURIComponent(id)}/resume`),
  shortNoticeApprovals: () =>
    get<{ approvals: WaitlistShortNoticeApproval[] }>("/waitlist/short-notice-approvals"),

  decideShortNoticeApproval: (
    approvalId: string,
    decision: "approve" | "reject" | "revoke",
  ) =>
    send<{ status: "approved" | "rejected" | "revoked" }>(
      "POST",
      `/waitlist/short-notice-approvals/${encodeURIComponent(approvalId)}/decision`,
      { decision },
    ),

  requests: (
    status?: WaitlistRequestStatus,
    page?: number,
    limit?: number,
    scope?: "mine" | "organisation",
  ) =>
    get<{
      requests: WaitlistRequest[];
      total?: number;
      page?: number;
      limit?: number;
      totalPages?: number;
    }>("/waitlist/requests", {
      status,
      ...(scope ? { scope } : {}),
      ...(page === undefined ? {} : { page: String(page) }),
      ...(limit === undefined ? {} : { limit: String(limit) }),
    }),

  createRequest: (input: {
    studentId: string;
    tutorId: string;
    serviceId: number;
    serviceName: string;
    patterns: WaitlistPattern[];
    shortNoticeOptIn?: boolean;
  }) => send("POST", "/waitlist/requests", input),

  approveRequest: (requestId: string) =>
    send("POST", `/waitlist/requests/${encodeURIComponent(requestId)}/approve`),

  /**
   * The reason, where a tutor gave one, is what the family's message carries.
   *
   * Optional: "no" is sometimes the whole answer, and a required box would
   * only produce a lot of "n/a".
   */
  rejectRequest: (requestId: string, reason?: string) =>
    send("POST", `/waitlist/requests/${encodeURIComponent(requestId)}/reject`, {
      ...(reason && reason.trim() !== "" ? { reason: reason.trim() } : {}),
    }),

  notifications: () =>
    get<{ notifications: WaitlistNotification[] }>("/waitlist/notifications"),

  retentionFollowUpPreferences: (studentId: string) =>
    get<FamilyRetentionFollowUpPreferences>(
      `/waitlist/notification-preferences/${encodeURIComponent(studentId)}/retention-follow-ups`,
    ),

  saveRetentionFollowUpPreferences: (
    studentId: string,
    input: FamilyRetentionFollowUpPreferencesInput,
  ) =>
    send<FamilyRetentionFollowUpPreferences>(
      "PUT",
      `/waitlist/notification-preferences/${encodeURIComponent(studentId)}/retention-follow-ups`,
      input,
    ),

  offerContext: (offerId: string, studentId: string) => get<WaitlistOfferContext>(`/waitlist/offers/${encodeURIComponent(offerId)}`, { studentId }),

  markOfferRead: (offerId: string) =>
    send<void>("POST", `/waitlist/offers/${encodeURIComponent(offerId)}/read`),

  acceptOffer: (offerId: string) =>
    send<{ status: "accepted"; booking?: unknown }>(
      "POST",
      `/waitlist/offers/${encodeURIComponent(offerId)}/accept`,
    ),

  rejectOffer: (offerId: string) =>
    send<{ status: "rejected" }>(
      "POST",
      `/waitlist/offers/${encodeURIComponent(offerId)}/reject`,
    ),
};
